import { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { usePreferences, useSession } from '@/hooks';
import { Button, Panel } from '@/ui';
import { applyPreferences } from '@/theme/applyPreferences';
import { darkTheme, lightTheme } from '@/theme/tokens';
import { ALL_APPEARANCE_KEYS, appearanceValues } from './appearanceCategories';
import { AppearancePreview } from './AppearancePreview';
import { readLooks, writeLooks, type SavedLook } from './appearanceLooksStorage';

const Controls = styled(Panel)`
  display: grid;
  gap: 12px;
  margin-bottom: 16px;
  h3,
  p {
    margin: 0;
  }
  p {
    color: ${({ theme }) => theme.color.textMuted};
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
  label {
    display: grid;
    gap: 6px;
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
  input,
  select {
    width: 100%;
    min-width: 0;
    padding: 9px;
    border-radius: ${({ theme }) => theme.radius.sm};
    background: ${({ theme }) => theme.color.surfaceChrome};
    color: ${({ theme }) => theme.color.textPrimary};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
  }
  input:focus-visible,
  select:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
  > div {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

export function SavedLooks() {
  const { currentUser } = useSession();
  return <AccountLooks key={currentUser?.id ?? 'guest'} owner={currentUser?.id ?? 'guest'} />;
}
function AccountLooks({ owner }: { owner: string }) {
  const { prefs, updatePreferences } = usePreferences();
  const [looks, setLooks] = useState(() => readLooks(owner));
  const [name, setName] = useState('');
  const [managing, setManaging] = useState(false);
  const [selected, setSelected] = useState('');
  const [preview, setPreview] = useState<{ look: SavedLook; baseline: string } | null>(null);
  const [message, setMessage] = useState('');
  const baseline = JSON.stringify(appearanceValues(ALL_APPEARANCE_KEYS, prefs));
  if (preview && preview.baseline !== baseline) setPreview(null);
  const active = looks.find((look) => look.id === selected);
  function persist(next: SavedLook[]) {
    if (!writeLooks(owner, next)) {
      setMessage('This browser could not save your looks. Try freeing browser storage.');
      return false;
    }
    setLooks(next);
    return true;
  }
  const projected = preview ? { ...prefs, ...preview.look.values } : prefs;
  const theme = applyPreferences(
    projected.themeMode === 'dark' ? darkTheme : lightTheme,
    projected,
  );
  return (
    <>
      <Controls>
        <div style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <h3>Saved looks</h3>
          <Button variant="ghost" aria-expanded={managing} onClick={() => setManaging(!managing)}>
            {managing ? 'Close manager' : 'Manage looks'}
          </Button>
        </div>
        {managing && (
          <>
            <p>Save up to 12 looks for this account in this browser. Preview before applying.</p>
            <label>
              Name this look
              <input
                maxLength={40}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Evening glow"
              />
            </label>
            <Button
              disabled={!name.trim() || looks.length >= 12}
              onClick={() => {
                if (
                  looks.some(
                    (look) => look.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase(),
                  )
                ) {
                  setMessage('Choose a different name for this look.');
                  return;
                }
                const look = {
                  id: crypto.randomUUID(),
                  name: name.trim(),
                  values: appearanceValues(ALL_APPEARANCE_KEYS, prefs),
                };
                if (persist([...looks, look])) {
                  setSelected(look.id);
                  setName('');
                  setMessage('Look saved.');
                }
              }}
            >
              Save current look
            </Button>
            {looks.length > 0 && (
              <>
                <label>
                  Choose a saved look
                  <select
                    value={selected}
                    onChange={(e) => {
                      setSelected(e.target.value);
                      setPreview(null);
                    }}
                  >
                    <option value="">Select a look</option>
                    {looks.map((look) => (
                      <option key={look.id} value={look.id}>
                        {look.name}
                      </option>
                    ))}
                  </select>
                </label>
                <div>
                  <Button
                    disabled={!active}
                    onClick={() => {
                      if (active) setPreview({ look: active, baseline });
                    }}
                  >
                    Preview look
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={!active}
                    onClick={() => {
                      if (persist(looks.filter((look) => look.id !== selected))) {
                        setPreview(null);
                        setSelected('');
                        setMessage('Look deleted.');
                      }
                    }}
                  >
                    Delete look
                  </Button>
                </div>
              </>
            )}
          </>
        )}
        {preview && (
          <div>
            <Button
              onClick={() => {
                void updatePreferences(preview.look.values);
                setPreview(null);
                setMessage('Look applied.');
              }}
            >
              Apply look
            </Button>
            <Button variant="ghost" onClick={() => setPreview(null)}>
              Cancel preview
            </Button>
          </div>
        )}
        {message && <p role="status">{message}</p>}
      </Controls>
      {preview && (
        <p role="status">Previewing {preview.look.name}. Your settings have not changed.</p>
      )}
      <ThemeProvider theme={theme}>
        <AppearancePreview />
      </ThemeProvider>
    </>
  );
}
